import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  FormInstance,
  Input,
  InputNumber,
  message,
  Row,
  Select,
} from 'antd';
import { sumBy } from 'lodash';
import React from 'react';

import { REGIONS } from '../../constants/REGIONS';
import { fetchAPI } from '../../utils/api';
import QuotationBuyerEntity from './QuotationBuyerEntity';

const layout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 16 },
};

const formItemLayoutWithOutLabel = {
  wrapperCol: { span: 16, offset: 6 },
};

const validateMessages = {
  required: 'This field is required!',
  types: {
    email: 'Not a validate email!',
    number: 'Not a validate number!',
  },
  number: {
    range: 'Must be between ${min} and ${max}', // eslint-disable-line
  },
};

export default (props: {
  form: FormInstance;
  regions: string[];
  onOk: (payload: any) => void;
  visible: boolean;
}) => {
  const { form, regions } = props;
  const id = form.getFieldValue('id');

  const onFinish = async formData => {
    const hide = message.loading('Saving...', 0);
    const payload = {
      id,
      ...formData,
      tax: (sumBy(formData.lines, 'total') * (formData.tax_rate ?? 0)) / 100,
      lines: formData.lines.map(line => {
        const { id, name, description, unit_price, quantity } = line;
        return {
          id,
          name,
          unit_price,
          quantity,
          description,
        };
      }),
    };
    try {
      const product = await fetchAPI('quotations', {
        payload: payload,
        method: 'PUT',
      });
      form.resetFields();
      props.onOk(product);
      message.success('Save success');
    } catch (e) {
    } finally {
      hide();
    }
  };

  return (
    <Form
      {...layout}
      form={form}
      name="basic"
      onFinish={onFinish}
      validateMessages={validateMessages}
    >
      <Form.Item
        label="Reference Number"
        name="reference_number"
        rules={[{ type: 'string', required: true }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Validity Date"
        name="validity_date"
        rules={[{ type: 'object', required: true }]}
      >
        <DatePicker />
      </Form.Item>
      <Form.Item
        label="Issue Date"
        name="issue_date"
        rules={[{ type: 'object', required: true }]}
      >
        <DatePicker />
      </Form.Item>
      <Form.Item
        label="Select Buyer"
        name="entity_buyer_id"
        rules={[{ type: 'number', required: true }]}
      >
        <QuotationBuyerEntity
          visible={props.visible}
          onSelect={payload => {
            form.setFieldsValue(payload);
          }}
          defaultValue={{
            id: form.getFieldValue('entity_buyer_id'),
            name: form.getFieldValue('buyer_name'),
          }}
        />
      </Form.Item>
      <Form.Item
        label="Buyer’s Name"
        name="buyer_name"
        rules={[{ type: 'string', required: true }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Buyer’s Email"
        name="buyer_email"
        rules={[{ type: 'string', required: true }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Buyer’s Phone"
        name="buyer_phone"
        rules={[{ type: 'string', required: true }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Buyer’s Address"
        name="buyer_address"
        rules={[{ type: 'string', required: true }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Buyer's Company Name"
        name="buyer_company_name"
        rules={[{ type: 'string', required: true }]}
      >
        <Input />
      </Form.Item>
      <Form.Item label="Currency" name="currency" rules={[{ required: true }]}>
        <Select
          placeholder="Select currency"
          onChange={currency => {
            form.setFieldsValue({
              currency,
            });
          }}
        >
          {regions.map(regi => {
            const key = regi.trim();
            return (
              <Select.Option value={REGIONS[key]} key={key}>
                {`${key} (${REGIONS[key]})`}
              </Select.Option>
            );
          })}
        </Select>
      </Form.Item>
      <Form.Item
        label="Tax (%)"
        name="tax_rate"
        rules={[{ type: 'number', required: true, min: 0 }]}
      >
        <InputNumber />
      </Form.Item>

      <Divider orientation="left" plain>
        Line Items
      </Divider>

      <Form.List name="lines">
        {(fields, { add, remove }) => {
          const lines = form.getFieldValue('lines');
          const totalLines = sumBy(lines, 'total');
          const tax =
            ((form.getFieldValue('tax_rate') ?? 0) * totalLines) / 100;
          const total = totalLines + tax;
          return (
            <div>
              <Row style={{ marginBottom: 10 }}>
                <Col span={12}>
                  <b>Product</b>
                  <br />
                </Col>
                <Col span={4}>
                  <b>Quantity</b>
                  <br />
                </Col>

                <Col span={4}>
                  <b>Unit Price</b>
                  <br />
                </Col>
                <Col span={4}>
                  <b>Line Total</b>
                  <br />
                </Col>
              </Row>
              {fields.map((field, index) => {
                return (
                  <Form.Item
                    wrapperCol={{ span: 24 }}
                    required
                    key={`line-${index}`}
                  >
                    <Form.Item
                      {...field}
                      validateTrigger={['onChange', 'onBlur']}
                      rules={[
                        {
                          required: true,
                        },
                      ]}
                      name={[field.name, 'name']}
                      // @ts-ignore
                      fieldKey={[field.fieldKey, 'name']}
                      noStyle
                      key={`name-${index}`}
                    >
                      <Input
                        placeholder="Product Name"
                        onChange={e => {
                          let lines = form.getFieldValue('lines');
                          const value = e.target.value;
                          lines[field.fieldKey].name = value;
                          form.setFieldsValue({ lines });
                        }}
                        style={{ width: 'calc(50% - 10px)', marginRight: 10 }}
                      />
                    </Form.Item>

                    <Form.Item
                      {...field}
                      validateTrigger={['onChange', 'onBlur']}
                      rules={[
                        {
                          required: true,
                          type: 'number',
                        },
                      ]}
                      name={[field.name, 'quantity']}
                      // @ts-ignore
                      fieldKey={[field.fieldKey, 'quantity']}
                      noStyle
                      key={`quantity-${index}`}
                    >
                      <InputNumber
                        placeholder="Quantity"
                        onChange={quantity => {
                          const lines = form.getFieldValue('lines');
                          const line = lines[field.fieldKey];
                          const unitPrice = line.unit_price ?? 0;
                          const total = Number(quantity) * unitPrice;

                          line.quantity = quantity;
                          line.total = total;

                          form.setFieldsValue({ lines });
                        }}
                        parser={value => value.replace(/\$\s?|(,*)/g, '')}
                        style={{ width: 'calc(16% - 10px)', marginRight: 10 }}
                      />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, 'unit_price']}
                      // @ts-ignore
                      fieldKey={[field.fieldKey, 'unit_price']}
                      noStyle
                      key={`unit-price-${index}`}
                    >
                      <InputNumber
                        placeholder="Unit Price"
                        onChange={e => {
                          const lines = form.getFieldValue('lines');
                          const line = lines[field.fieldKey];
                          const unitPrice = Number(e ?? 0);
                          const quantity = line.quantity ?? 0;

                          line.unit_price = unitPrice;
                          line.total = Number(quantity) * unitPrice;

                          form.setFieldsValue({ lines });
                        }}
                        style={{ width: 'calc(16% - 10px)', marginRight: 10 }}
                      />
                    </Form.Item>

                    <Form.Item
                      {...field}
                      name={[field.name, 'total']}
                      // @ts-ignore
                      fieldKey={[field.fieldKey, 'total']}
                      noStyle
                      key={`total-${index}`}
                    >
                      <Input
                        placeholder="Line Total"
                        style={{ width: 'calc(16% - 10px)', marginRight: 10 }}
                        disabled
                      />
                    </Form.Item>
                    {fields.length > 1 ? (
                      <MinusCircleOutlined
                        className="dynamic-delete-button"
                        onClick={() => {
                          remove(field.name);
                        }}
                      />
                    ) : null}

                    <Form.Item
                      {...field}
                      name={[field.name, 'description']}
                      // @ts-ignore
                      fieldKey={[field.fieldKey, 'description']}
                      noStyle
                      key={`description-${index}`}
                    >
                      <Input.TextArea
                        placeholder="Product Description"
                        onChange={e => {
                          let lines = form.getFieldValue('lines');
                          const value = e.target.value;
                          lines[field.fieldKey].description = value;
                          form.setFieldsValue({ lines });
                        }}
                        style={{
                          width: 'calc(50% - 10px)',
                          marginRight: 10,
                          marginTop: 10,
                        }}
                      />
                    </Form.Item>
                  </Form.Item>
                );
              })}
              <Form.Item {...formItemLayoutWithOutLabel}>
                <Button
                  type="dashed"
                  onClick={() => {
                    add();
                  }}
                  style={{ width: '60%' }}
                >
                  <PlusOutlined /> Add Line
                </Button>
                <div
                  style={{ width: '60%', marginTop: 10, textAlign: 'center' }}
                >
                  <b>Total:</b> {parseFloat(String(total)).toFixed(2)}{' '}
                  {form.getFieldValue('currency')}
                </div>
              </Form.Item>
            </div>
          );
        }}
      </Form.List>
    </Form>
  );
};
